import { useQuery } from '@tanstack/react-query';
import {
  fetchQuestions,
  fetchQuestion,
  fetchRandomQuestions,
  fetchStats,
  fetchTopics,
  fetchSubtopics,
} from '../api/questions';
import { useCertStore } from '../stores/certStore';
import type { QuestionsParams } from '../types';

/** Fetch paginated & filtered question list. */
export function useQuestions(params?: QuestionsParams, enabled = true) {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery({
    queryKey: ['questions', certCode, params],
    queryFn: () => fetchQuestions(params, certCode),
    enabled,
  });
}

/** Fetch a single question by ID. */
export function useQuestion(id: number | null) {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery({
    queryKey: ['question', certCode, id],
    queryFn: () => fetchQuestion(id!),
    enabled: id !== null,
  });
}

/** Fetch N random questions (exam mode). */
export function useRandomQuestions(count: number, enabled = false) {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery({
    queryKey: ['questions', 'random', certCode, count],
    queryFn: () => fetchRandomQuestions(count, certCode),
    enabled,
    staleTime: Infinity, // Don't refetch once loaded
  });
}

/** Fetch global statistics. */
export function useStats() {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery({
    queryKey: ['stats', certCode],
    queryFn: () => fetchStats(certCode),
  });
}

/** Fetch distinct topics with counts. */
export function useTopics() {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery({
    queryKey: ['topics', certCode],
    queryFn: () => fetchTopics(certCode),
  });
}

/** Fetch the subtopic catalog with counts, optionally scoped to one domain. */
export function useSubtopics(topic?: string, enabled = true) {
  const certCode = useCertStore((s) => s.certCode);
  return useQuery({
    queryKey: ['subtopics', certCode, topic ?? null],
    queryFn: () => fetchSubtopics(certCode, topic),
    enabled,
  });
}
